import {
    AcceptInvitationToOrganizationMutation,
    AcceptInvitationToOrganizationMutationVariables,
    ChangeRoleMutation,
    ChangeRoleMutationVariables,
    RemoveMemberFromOrganizationMutation,
    RemoveMemberFromOrganizationMutationVariables,
    TeamType
} from "../../../API";
import {
    acceptInvitationToOrganization,
    changeRole,
    removeMemberFromOrganization,
} from "../../../graphql/mutations";

import { API } from "aws-amplify";
import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import { TeamManagerClient } from "./TeamManagerClient";

export class GraphQLTeamManagerClient implements TeamManagerClient {
    public static readonly CHANGE_ROLE_ATTEMPT = "GraphQLTeamManagerClient.changeRole.Attempt";
    public static readonly CHANGE_ROLE_SUCCESS = "GraphQLTeamManagerClient.changeRole.Success";
    public static readonly CHANGE_ROLE_FAILURE = "GraphQLTeamManagerClient.changeRole.Failure";
    public static readonly REMOVE_MEMBER_ATTEMPT = "GraphQLTeamManagerClient.removeMember.Attempt";
    public static readonly REMOVE_MEMBER_SUCCESS = "GraphQLTeamManagerClient.removeMember.Success";
    public static readonly REMOVE_MEMBER_FAILURE = "GraphQLTeamManagerClient.removeMember.Failure";
    public static readonly ACCEPT_INVITE_ATTEMPT = "GraphQLTeamManagerClient.acceptInvite.Attempt";
    public static readonly ACCEPT_INVITE_SUCCESS = "GraphQLTeamManagerClient.acceptInvite.Success";
    public static readonly ACCEPT_INVITE_FAILURE = "GraphQLTeamManagerClient.acceptInvite.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }
    public async changeRole(
        organizationId: string,
        userId: string,
        targetRole: TeamType
    ): Promise<void> {
        try {
            this.logger.info(`Changing role for organization ${organizationId}`, undefined, [GraphQLTeamManagerClient.CHANGE_ROLE_ATTEMPT]);
            const idToken = await this.idTokenSupplier.get();
            const variables: ChangeRoleMutationVariables = {
                input: {
                    organizationId,
                    userId,
                    targetRole
                },
            };
            const response = await API.graphql({
                query: changeRole,
                variables,
                authToken: idToken
            }) as GraphQLResult<ChangeRoleMutation>;
            this.logger.info(`Change role success: ${JSON.stringify(response)}`, undefined, [GraphQLTeamManagerClient.CHANGE_ROLE_SUCCESS]);
        } catch (error) {
            this.logger.error(`Error changing role for organization ${organizationId}`, error, [GraphQLTeamManagerClient.CHANGE_ROLE_FAILURE]);
            throw error;
        }
    }
    public async removeMember(organizationId: string, userId: string): Promise<void> {
        try {
            this.logger.info(`Removing member from organization ${organizationId}`, undefined, [GraphQLTeamManagerClient.REMOVE_MEMBER_ATTEMPT]);
            const idToken = await this.idTokenSupplier.get();
            const variables: RemoveMemberFromOrganizationMutationVariables = {
                input: {
                    organizationId,
                    userId,
                },
            };
            const response = await API.graphql({
                query: removeMemberFromOrganization,
                variables,
                authToken: idToken
            }) as GraphQLResult<RemoveMemberFromOrganizationMutation>;
            this.logger.info(`Remove member success: ${JSON.stringify(response)}`, undefined, [GraphQLTeamManagerClient.REMOVE_MEMBER_SUCCESS]);
        } catch (error) {
            this.logger.error(`Error removing member from organization ${organizationId}`, error, [GraphQLTeamManagerClient.REMOVE_MEMBER_FAILURE]);
            throw error;
        }
    }
    public async acceptInvite(invitationId: string, userId: string): Promise<void> {
        try {
            this.logger.info(`Accepting invite for invitation ${invitationId}`, undefined, [GraphQLTeamManagerClient.ACCEPT_INVITE_ATTEMPT]);
            const idToken = await this.idTokenSupplier.get();
            const variables: AcceptInvitationToOrganizationMutationVariables = {
                input: {
                    invitationId,
                    userId,
                },
            };
            const response = await API.graphql({
                query: acceptInvitationToOrganization,
                variables,
                authToken: idToken
            }) as GraphQLResult<AcceptInvitationToOrganizationMutation>;
            this.logger.info(`Accept invite success: ${JSON.stringify(response)}`, undefined, [GraphQLTeamManagerClient.ACCEPT_INVITE_SUCCESS]);
        } catch (error) {
            this.logger.error(`Error accepting invite for organization ${invitationId}`, error, [GraphQLTeamManagerClient.ACCEPT_INVITE_FAILURE]);
            throw error;
        }
    }
}
export const graphQLTeamManagerClient = new GraphQLTeamManagerClient(
    ClientLoggerFactory.getClientLogger("GraphQLTeamManagerClient"),
    AuthIDTokenSupplierFactory.getInstance()
);